import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  ThemeProvider,
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import MaterialTable from "material-table";
import { TableIcons } from "../../../../utils/TableIcons";
import { LocalizationTheme } from "../../../../utils/utils";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  deleteChecklistTemplate,
  getChecklistTemplates,
} from "./services/checklistTemplatesApisService";
import { DisplayMessage } from "../../../../services/helpers";
import DeleteConfirmationModal from "../../../Common/Modals/DeleteConfirmationModal";

const ChecklistTemplatesTable = (props) => {
  const { resources } = props;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const history = useHistory();
  const location = useLocation();

  const fetchChecklistTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getChecklistTemplates();
      if (!response) {
        setData([]);
        return;
      }
      const fetchedChecklistTemplates = response.map((template) => ({
        id: template?.id,
        description: template?.description,
        brand: template?.brand?.description || "",
        isActive: template?.isActive,
      }));
      setData(fetchedChecklistTemplates);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const refreshGrid = location.state?.refreshGrid || false;
    if (!data.length || refreshGrid) {
      fetchChecklistTemplates();
      if (refreshGrid) {
        history.replace({
          pathname: location.pathname,
          state: {},
        });
      }
    }
  }, [data.length, location, fetchChecklistTemplates, history]);

  const handleDelete = useCallback((id) => {
    setSelectedTemplate(id);
    setDeleteModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    (record) => {
      history.push(`/checklisttemplates/${record.id}`);
    },
    [history]
  );

  const handleCloseDelete = () => {
    setSelectedTemplate(null);
    setDeleteModalOpen(false);
  };

  const handleSubmitDelete = async () => {
    const result = await deleteChecklistTemplate(selectedTemplate);
    handleCloseDelete();
    if (result === true) {
      DisplayMessage(resources["TemplateWasDeletedWithSuccess"], "success");
      fetchChecklistTemplates();
    }
  };

  const tableSettings = useMemo(() => {
    return {
      columns: [
        { title: resources["Description"], field: "description" },
        { title: resources["Brand"], field: "brand" },
        {
          title: resources["IsActive"],
          field: "isActive",
          render: (rowData) => (rowData.isActive ? "True" : "False"),
        },
      ],
      actions: [
        {
          icon: () => <Edit />,
          tooltip: resources["EditTooltip"],
          onClick: (event, rowData) => handleEdit(rowData),
        },
        {
          icon: () => <DeleteOutline />,
          tooltip: resources["Delete"],
          onClick: (event, rowData) => handleDelete(rowData.id),
        },
      ],
      options: {
        emptyRowsWhenPaging: false,
        pageSize: 10,
        pageSizeOptions: [10, 15, 20],
      },
    };
  }, [resources, handleEdit, handleDelete]);

  return (
    <Box>
      <Box className="position-relative py-4">
        <Typography variant="h3">{resources["ChecklistTemplates"]}</Typography>
        <Box
          className={"offcentered-loader " + (loading ? "fadeIn" : "fadeOut")}
        >
          <CircularProgress />
        </Box>
        <Box className={!loading ? "fadeIn mt-4" : "fadeOut"}>
          <Box display="flex" justifyContent="flex-end">
            <Link
              to={{
                pathname: "/checklisttemplates/new",
              }}
              style={{ textDecoration: "none" }}
            >
              <Button
                id="add-checklist-template"
                variant="contained"
                color="primary"
              >
                <i
                  className="fas fa-plus mr-2"
                  title={resources["AddNewChecklistTemplate"]}
                />
                {resources["AddNewChecklistTemplate"]}
              </Button>
            </Link>
          </Box>
          <Paper elevation={3} className="p-3" style={{ marginTop: "26px" }}>
            <ThemeProvider theme={LocalizationTheme()}>
              <MaterialTable
                icons={TableIcons}
                title=""
                localization={{
                  header: { actions: resources["Actions"] },
                  body: {
                    emptyDataSourceMessage: resources["NoRecordsToBeDisplayed"],
                    deleteTooltip: resources["DeleteTooltip"],
                    editTooltip: resources["EditTooltip"],
                  },
                  toolbar: {
                    searchPlaceholder: resources["Search"],
                    searchTooltip: resources["Search"],
                  },
                  pagination: {
                    firstTooltip: resources["FirstTooltip"],
                    lastTooltip: resources["LastTooltip"],
                    previousTooltip: resources["PreviousTooltip"],
                    nextTooltip: resources["NextTooltip"],
                    labelRowsPerPage: resources["LabelRowsPerPage"],
                    labelRowsSelect: resources["LabelRowsSelect"],
                  },
                }}
                data={data}
                columns={tableSettings.columns}
                actions={tableSettings.actions}
                options={tableSettings.options}
              />
            </ThemeProvider>
          </Paper>
        </Box>
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleSubmitDelete}
          resources={resources}
          areYouSureMessage={
            resources["AreYouSureYouWantToDeleteChecklistTemplate"]
          }
        />
      </Box>
    </Box>
  );
};

export default ChecklistTemplatesTable;
